/**
 * NEXUMA — User Management Page (Hub i Qasjeve)
 *
 * Three-tab hub — një pikë e vetme për menaxhimin e qasjeve:
 *  Tab 0 — Përdoruesit        : users list + add/edit dialogs + custom roles
 *  Tab 1 — Rolet & Privilegjet : full RolesPermissionsPage (embedded)
 *  Tab 2 — Pozitat & Qasjet    : PositionsPage (embedded)
 *
 * Tab-i aktiv drejtohet nga query-param `?tab=` që lidhjet e vjetra
 * (/roles-permissions, /positions) ridrejtohen këtu me tab-in e saktë.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import Box              from '@mui/material/Box';
import Paper            from '@mui/material/Paper';
import Typography       from '@mui/material/Typography';
import Stack            from '@mui/material/Stack';
import Tabs             from '@mui/material/Tabs';
import Tab              from '@mui/material/Tab';
import Table            from '@mui/material/Table';
import TableBody        from '@mui/material/TableBody';
import TableCell        from '@mui/material/TableCell';
import TableHead        from '@mui/material/TableHead';
import TableRow         from '@mui/material/TableRow';
import Chip             from '@mui/material/Chip';
import Avatar           from '@mui/material/Avatar';
import Button           from '@mui/material/Button';
import IconButton       from '@mui/material/IconButton';
import Tooltip          from '@mui/material/Tooltip';
import TextField        from '@mui/material/TextField';
import InputAdornment   from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch           from '@mui/material/Switch';
import Checkbox         from '@mui/material/Checkbox';
import Divider          from '@mui/material/Divider';
import Alert            from '@mui/material/Alert';
import Snackbar         from '@mui/material/Snackbar';
import Dialog           from '@mui/material/Dialog';
import DialogTitle      from '@mui/material/DialogTitle';
import DialogContent    from '@mui/material/DialogContent';
import DialogActions    from '@mui/material/DialogActions';
import MenuItem         from '@mui/material/MenuItem';
import Grid             from '@mui/material/Grid';
import Card             from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';

import GroupIcon           from '@mui/icons-material/Group';
import PersonAddAltIcon    from '@mui/icons-material/PersonAddAlt';
import PersonOffIcon       from '@mui/icons-material/PersonOff';
import SearchIcon          from '@mui/icons-material/Search';
import EditIcon            from '@mui/icons-material/Edit';
import AlternateEmailIcon  from '@mui/icons-material/AlternateEmail';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import BlockIcon           from '@mui/icons-material/Block';
import SaveIcon            from '@mui/icons-material/Save';
import LockResetIcon       from '@mui/icons-material/LockReset';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SecurityIcon        from '@mui/icons-material/Security';
import EmailIcon           from '@mui/icons-material/Email';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon    from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon  from '@mui/icons-material/HourglassEmpty';
import InfoOutlinedIcon    from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon     from '@mui/icons-material/ContentCopy';

import BadgeIcon           from '@mui/icons-material/Badge';

import DeleteIcon          from '@mui/icons-material/DeleteOutline';
import ForwardToInboxIcon  from '@mui/icons-material/ForwardToInboxOutlined';
import AddIcon             from '@mui/icons-material/Add';
import ConfirmDialog       from '../components/common/ConfirmDialog';
import apiService, { authFetch } from '../services/apiServices';
import RolesPermissionsPage from './RolesPermissionsPage';
import PositionsPage        from './PositionsPage';
import { accountStatus, accountStatusChip, formatDMY } from '../utils/activationStatus';

// ── Constants (shared with Company.js) ───────────────────────────────────────

const ROLE_LABELS = { admin:'Admin', manager:'Menaxher', hr:'HR', payroll:'Payroll', employee:'Punëtor', staff:'Staff' };
const ROLE_COLORS = { admin:'error', manager:'warning', hr:'primary', payroll:'secondary', employee:'success', staff:'default' };

const ROLES_RBAC = [
  { id:'admin',    name:'Administrator', color:'#DC2626', desc:'Qasje totale: konfigurim, fshirje, export, RBAC' },
  { id:'hr',       name:'HR Manager',    color:'#D97706', desc:'Punëtorë, kontratat, pushimet, pagat, trajnimet' },
  { id:'payroll',  name:'Payroll',       color:'#3E8FA3', desc:'Vetëm pagat, eksport ATK/KBPK' },
  { id:'manager',  name:'Menaxher',      color:'#0891B2', desc:'Shikon ekipin e vet, aprovat pushimet & orët shtesë' },
  { id:'employee', name:'Punëtor',       color:'#3E8FA3', desc:'Vetëm të dhënat e veta: slip paga, pushime, orari' },
  { id:'staff',    name:'Staff',         color:'#6B7280', desc:'Qasje bazë e kufizuar' },
];

const MODULE_DEFS = [
  { key:'workers',    label:'Punëtorët',  group:'HR',        levels:[{value:'none',label:'Asnjë',color:'#9CA3AF',desc:'Pa qasje'},{value:'self',label:'Vetë',color:'#0891B2',desc:'Vetëm profilin e vet'},{value:'team',label:'Ekipi',color:'#D97706',desc:'Ekipi i vet'},{value:'full',label:'Të gjithë',color:'#16A34A',desc:'Qasje e plotë'}]},
  { key:'contracts',  label:'Kontratat',  group:'HR',        levels:[{value:'none',label:'Asnjë',color:'#9CA3AF',desc:'Pa qasje'},{value:'self',label:'Kontrata vetë',color:'#0891B2',desc:'Kontrata e vet'},{value:'team',label:'Ekipi',color:'#D97706',desc:'Ekipi i vet'},{value:'full',label:'Të gjitha',color:'#16A34A',desc:'Qasje e plotë'}]},
  { key:'salaries',   label:'Pagat',      group:'Financa',   levels:[{value:'none',label:'Asnjë',color:'#9CA3AF',desc:'Pa qasje'},{value:'slip',label:'Slip vetë',color:'#0891B2',desc:'Vetëm slip-in e vet'},{value:'team',label:'Ekipi',color:'#D97706',desc:'Ekipi i vet'},{value:'full',label:'Të gjitha',color:'#16A34A',desc:'Qasje e plotë'}]},
  { key:'holidays',   label:'Pushimet',   group:'HR',        levels:[{value:'none',label:'Asnjë',color:'#9CA3AF',desc:'Pa qasje'},{value:'request',label:'Kërko vetë',color:'#0891B2',desc:'Kërkon pushim'},{value:'approve',label:'Aprovo ekip',color:'#D97706',desc:'Aprovon pushimet'},{value:'full',label:'Menaxho',color:'#16A34A',desc:'Menaxhim i plotë'}]},
  { key:'presence',   label:'Prezenca',   group:'HR',        levels:[{value:'none',label:'Asnjë',color:'#9CA3AF',desc:'Pa qasje'},{value:'self',label:'Vetë',color:'#0891B2',desc:'Check-in/out vetë'},{value:'team',label:'Ekipi',color:'#D97706',desc:'Ekipi i vet'},{value:'full',label:'Të gjithë',color:'#16A34A',desc:'Qasje e plotë'}]},
  { key:'trainings',  label:'Trajnimet',  group:'HR',        levels:[{value:'none',label:'Asnjë',color:'#9CA3AF',desc:'Pa qasje'},{value:'self',label:'Vetë',color:'#0891B2',desc:'Trajnimet e veta'},{value:'team',label:'Ekipi',color:'#D97706',desc:'Ekipi i vet'},{value:'full',label:'Menaxho',color:'#16A34A',desc:'Menaxhim i plotë'}]},
  { key:'reports',    label:'Raportet',   group:'Analitikë', levels:[{value:'none',label:'Asnjë',color:'#9CA3AF',desc:'Pa qasje'},{value:'self',label:'Vetë',color:'#0891B2',desc:'Raportet personale'},{value:'team',label:'Ekipi',color:'#D97706',desc:'Ekipi i vet'},{value:'full',label:'Të gjitha',color:'#16A34A',desc:'Qasje e plotë'}]},
  { key:'compliance', label:'Compliance', group:'Analitikë', levels:[{value:'none',label:'Asnjë',color:'#9CA3AF',desc:'Pa qasje'},{value:'read',label:'Shiko',color:'#0891B2',desc:'Lexon'},{value:'full',label:'Menaxho',color:'#16A34A',desc:'Menaxhim i plotë'}]},
  { key:'settings',   label:'Konfigurim', group:'Sistemi',   levels:[{value:'none',label:'Asnjë',color:'#9CA3AF',desc:'Pa qasje'},{value:'read',label:'Shiko',color:'#0891B2',desc:'Lexon'},{value:'full',label:'Admin',color:'#DC2626',desc:'Qasje totale'}]},
  { key:'audit',      label:'Audit Log',  group:'Sistemi',   levels:[{value:'none',label:'Asnjë',color:'#9CA3AF',desc:'Pa qasje'},{value:'read',label:'Shiko',color:'#0891B2',desc:'Lexon'},{value:'export',label:'Eksport',color:'#16A34A',desc:'Eksporton'}]},
];

const ROLE_DEFAULT_PERMS = {
  admin:    { workers:'full',    contracts:'full',  salaries:'full',  holidays:'full',    presence:'full',  trainings:'full',  reports:'full',  compliance:'full',  settings:'full',  audit:'export' },
  hr:       { workers:'full',    contracts:'full',  salaries:'team',  holidays:'full',    presence:'full',  trainings:'full',  reports:'full',  compliance:'read',  settings:'none',  audit:'read'   },
  payroll:  { workers:'none',    contracts:'none',  salaries:'full',  holidays:'none',    presence:'none',  trainings:'none',  reports:'team',  compliance:'none',  settings:'none',  audit:'none'   },
  manager:  { workers:'team',    contracts:'team',  salaries:'none',  holidays:'approve', presence:'team',  trainings:'team',  reports:'team',  compliance:'none',  settings:'none',  audit:'none'   },
  employee: { workers:'self',    contracts:'self',  salaries:'slip',  holidays:'request', presence:'self',  trainings:'self',  reports:'self',  compliance:'none',  settings:'none',  audit:'none'   },
  staff:    { workers:'self',    contracts:'self',  salaries:'slip',  holidays:'request', presence:'self',  trainings:'self',  reports:'self',  compliance:'none',  settings:'none',  audit:'none'   },
};

const CUSTOM_ROLE_COLORS = [
  '#DC2626','#D97706','#CA8A04','#16A34A','#059669',
  '#0891B2','#2563EB','#7C3AED','#9333EA','#DB2777',
  '#6B7280','#111827',
];

const BLANK_USER        = { userName:'', email:'', firstName:'', lastName:'', role:'staff', phone:'', jobTitle:'', sendInvite:true };
const BLANK_CUSTOM_ROLE = { name:'', description:'', color:'#7C3AED', scope:'company', permissions:{} };

// ── Helpers ──────────────────────────────────────────────────────────────────
const normalizeRoleValue = (value='') => {
  const map = {
    admin:'admin', administrator:'admin',
    manager:'manager', menaxher:'manager',
    hr:'hr', 'hr manager':'hr',
    payroll:'payroll',
    employee:'employee', punetor:'employee', 'punëtor':'employee',
    staff:'staff',
  };
  return map[(value?.toString().trim().toLowerCase()||'')] || 'staff';
};

const parsePermissions = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return null; }
};

const normalizeUser = (row={}) => {
  const role = normalizeRoleValue(row.user_role || row.UserRole || row.role || row.Role || 'staff');
  return {
    id: row.id || row.Id,
    userName: row.user_name || row.UserName || row.email || row.Email || '',
    email: row.email || row.Email || '',
    firstName: row.firstname || row.firstName || row.name || row.Name || '',
    lastName: row.lastname || row.lastName || row.surname || row.Surname || '',
    role, originalRole: role,
    phone: row.phone || row.Phone || '',
    jobTitle: row.job_title || row.JobTitle || row.position || row.Position || '',
    notes: row.notes || row.Notes || '',
    active: row.active !== undefined ? row.active : row.Active !== undefined ? row.Active : true,
    worker_id: row.worker_id || row.WorkerId || null,
    permissions: parsePermissions(row.permissions || row.Permissions),
    customRoleId: row.custom_role_id ?? row.customRoleId ?? null,
    // Data e aktivizimit e punëtorit të lidhur + verdikti i serverit («sot» i kompanisë).
    activationDate: row.activation_date || row.ActivationDate || null,
    activationStatus: row.activation_status || row.ActivationStatus || null,
    // Kush s'ka hyrë kurrë mund të fshihet (nëse s'ka gjurmë në histori);
    // kush ka hyrë, vetëm çaktivizohet.
    lastLoginAt: row.last_login_at || row.LastLoginAt || null,
  };
};

const buildPayload = (u) => ({
  Id: u.id, UserName: u.userName, Email: u.email,
  Name: u.firstName, Surname: u.lastName,
  // Roli dërgohet VETËM kur ka ndryshuar vërtet: serveri e ndalon ndryshimin e
  // rolit të vetvetes, dhe formulari që e dërgonte gjithmonë e bënte të
  // pamundur për adminin të ruante qoftë edhe numrin e vet të telefonit.
  ...(normalizeRoleValue(u.role) !== normalizeRoleValue(u.originalRole ?? u.role)
    ? { Role: normalizeRoleValue(u.role) } : {}),
  Phone: u.phone, JobTitle: u.jobTitle, Notes: u.notes,
  Active: u.active !== false,
  // Lidhja me punëtorin: pa të, pozita e tij nuk jep asnjë leje dhe porta e
  // aktivizimit nuk e di kur nis puna. `null` e shkëput lidhjen.
  worker_id: u.worker_id ?? null,
  Permissions: u.permissions ? JSON.stringify(u.permissions) : null,
  // Custom role that the backend actually enforces (services/effectivePermissions).
  // null clears it → user falls back to their base role.
  CustomRoleId: u.customRoleId ?? null,
});

const initials = (name='') => name.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2) || '?';
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email||'').trim());

// Delegate to the shared session reader: it checks sessionStorage → cookie →
// localStorage. The old hand-rolled version missed the cookie, so rememberMe /
// Electron sessions sent no token → silent 401 on writes. See frontend_session_token.

// `authFetch` e rinovon tokenin te 401-shi: me `fetch` të zhveshur, pas ~30
// minutash me tab të hapur çdo RUAJTJE dështonte me një «Gabim.» të pashpjegueshëm,
// ndërkohë që leximet vazhdonin të punonin.
async function putUser(userId, payload) {
  const r = await authFetch(`/api/users/${userId}`, {
    method:'PUT',
    headers:{'Content-Type':'application/json'},
    body: JSON.stringify(payload),
  });
  const data = await r.json().catch(() => ({}));
  // Teksti i serverit («administratori i fundit aktiv…», «email-i ekziston…»)
  // humbiste te një «Gabim.» i vetëm, pa asnjë të dhënë se çfarë të rregullohej.
  if (!r.ok || data?.success === false) throw new Error(data?.error || `Gabim (HTTP ${r.status})`);
  return data;
}

async function sendInviteEmail(userId, { now = false } = {}) {
  const r = await authFetch(`/api/users/${userId}/send-invite`,{
    method:'POST', headers:{'Content-Type':'application/json'},
    body: JSON.stringify({ now }),
  });
  const data = await r.json();
  if (!data?.success) throw new Error(data?.error || 'Dërgimi i ftesës dështoi');
  // { emailSent:boolean, setLink?:string } — emailSent=false kur SMTP s'është konfiguruar.
  return data;
}

// ── PermissionEditor ─────────────────────────────────────────────────────────
function PermissionEditor({ permissions, onChange }) {
  const perms  = permissions || {};
  const groups = [...new Set(MODULE_DEFS.map(m=>m.group))];
  return (
    <Box sx={{ border:'1px solid', borderColor:'divider', borderRadius:'8px', overflow:'hidden' }}>
      {groups.map((group, gi) => (
        <Box key={group}>
          <Box sx={{ px:1.5, py:0.6, bgcolor:'grey.50', borderTop: gi>0?'1px solid':'none', borderBottom:'1px solid', borderColor:'divider' }}>
            <Typography variant="caption" sx={{ fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:0.6, color:'text.disabled' }}>
              {group}
            </Typography>
          </Box>
          {MODULE_DEFS.filter(m=>m.group===group).map((mod, i, arr) => {
            const current      = perms[mod.key] || 'none';
            const currentLevel = mod.levels.find(l=>l.value===current) || mod.levels[0];
            return (
              <Box key={mod.key} sx={{ px:1.5, py:0.9, borderBottom: i<arr.length-1?'1px solid':'none', borderColor:'divider', '&:hover':{bgcolor:'action.hover'} }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb:0.75 }}>
                  <Typography variant="caption" fontWeight={700} sx={{ fontSize:12, color:'text.primary', minWidth:90 }}>{mod.label}</Typography>
                  <Typography variant="caption" sx={{ fontSize:11, color:currentLevel.color, fontStyle:'italic', textAlign:'right', flex:1, ml:1 }}>{currentLevel.desc}</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {mod.levels.map(l => {
                    const selected = current===l.value;
                    return (
                      <Tooltip key={l.value} title={l.desc} placement="top" arrow>
                        <Box onClick={()=>onChange(mod.key,l.value)} sx={{
                          px:1.25, py:0.3, borderRadius:'999px', cursor:'pointer', fontSize:11,
                          fontWeight: selected?700:500, border:'1.5px solid',
                          borderColor: selected?l.color:'#D1D5DB',
                          bgcolor: selected?l.color:'transparent',
                          color: selected?'#fff':l.color,
                          transition:'all .15s ease', userSelect:'none', whiteSpace:'nowrap',
                          '&:hover':{ borderColor:l.color, bgcolor: selected?l.color:l.bg||l.color+'15', color: selected?'#fff':l.color },
                        }}>
                          {selected && '✓ '}{l.label}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

// ── UsersPanel ───────────────────────────────────────────────────────────────
function UsersPanel() {
  const [snack, setSnack]       = useState('');
  const [snackType, setSnackType] = useState('success');
  const [manualInvite, setManualInvite] = useState({ link:'', message:'', sent:false, name:'', email:'' });
  const closeInviteLink = () => setManualInvite({ link:'', message:'', sent:false, name:'', email:'' });
  const showSnack = (msg, type='success') => { setSnack(msg); setSnackType(type); };

  // Ftesa shkon me email. Linku shfaqet vetëm kur email-i s'doli, që admini
  // të mos mbetet pa rrugë.
  const showInviteResult = (result, prefix='', user=null) => {
    const who = user ? { name: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.userName, email: user.email } : { name:'', email:'' };
    if (result?.scheduled) {
      closeInviteLink();
      showSnack(`${prefix}${result.message || 'Ftesa u planifikua.'}`, 'info');
    } else if (result?.emailSent === true) {
      closeInviteLink();
      showSnack(`${prefix}Ftesa u dërgua${who.email ? ` te ${who.email}` : ''}.`, 'success');
    } else if (result?.emailSent === false && result.setLink) {
      setManualInvite({ link: result.setLink, sent: false, message: result.message || 'Email-i nuk u dërgua.', ...who });
    } else if (result?.emailSent === false) {
      showSnack(`${prefix}${result.message || 'Email-i nuk u dërgua.'}`, 'warning');
    } else showSnack('Statusi i dërgimit nuk u konfirmua. Provo përsëri.', 'warning');
  };

  const [resendingId, setResendingId] = useState(null);
  const resendInvite = async (user) => {
    setResendingId(user.id);
    try { showInviteResult(await sendInviteEmail(user.id, { now: true }), '', user); }
    catch (e) { showSnack(e?.message || 'Dërgimi i ftesës dështoi.', 'error'); }
    setResendingId(null);
  };

  // ── Users state ──
  const [users, setUsers]               = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userSavingId, setUserSavingId] = useState(null);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUser, setNewUser]           = useState(BLANK_USER);
  const [addUserDialog, setAddUserDialog] = useState(false);

  // ── Details dialog ──
  const [detailsDialog, setDetailsDialog]   = useState({ open:false, user:null });
  const [detailsSaving, setDetailsSaving]   = useState(false);
  const [sendingAccessLink, setSendingAccessLink] = useState(false);

  // ── Email change dialog ──
  const [emailChangeDialog, setEmailChangeDialog] = useState({ open:false, user:null });
  const [emailChangeData, setEmailChangeData]     = useState({ newEmail:'', confirmEmail:'' });
  const [emailCheckResult, setEmailCheckResult]   = useState(null);
  const [emailChangeSaving, setEmailChangeSaving] = useState(false);

  // ── Custom roles state ──
  const [customRoles, setCustomRoles]           = useState([]);
  const [customRolesLoading, setCustomRolesLoading] = useState(false);
  const [customRoleDialog, setCustomRoleDialog] = useState({ open:false, role:null });
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userDeleting, setUserDeleting] = useState(false);
  const [checkingDeleteId, setCheckingDeleteId] = useState(null);
  // Lista e punëtorëve për lidhjen llogari↔punëtor (pa të, lejet e pozitës,
  // prezenca dhe pushimet e atij njeriu nuk gjejnë dot profilin e tij).
  const [workerOptions, setWorkerOptions] = useState([]);
  // Çaktivizimi e mbyll hyrjen menjëherë — pyetet para, me emrin e llogarisë.
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [bulkActivating, setBulkActivating] = useState(false);
  const [manageRoles, setManageRoles]   = useState(false);
  const [customRoleSaving, setCustomRoleSaving] = useState(false);

  // ── Data loading ──
  const loadUsers = useCallback(() => {
    setUsersLoading(true);
    apiService().get('users')
      .then(r => { if (r?.success) setUsers((r.data||[]).map(normalizeUser)); })
      .catch(()=>setUsers([]))
      .finally(()=>setUsersLoading(false));
  }, []);

  const loadCustomRoles = useCallback(() => {
    setCustomRolesLoading(true);
    apiService().get('customRoles')
      .then(r => { if (r?.success) setCustomRoles(r.data||[]); })
      .catch(()=>{})
      .finally(()=>setCustomRolesLoading(false));
  }, []);

  const loadWorkerOptions = useCallback(async () => {
    try {
      const r = await apiService().get('workers');
      const list = (r?.data || []).map(w => ({
        id: w.Id ?? w.id,
        name: `${w.FirstName || w.first_name || ''} ${w.LastName || w.last_name || ''}`.trim(),
        email: w.Email || w.email || '',
        workEmail: w.WorkEmail || w.work_email || '',
        active: w.Active !== false && w.active !== false,
      })).filter(w => w.id);
      list.sort((a, b) => a.name.localeCompare(b.name, 'sq'));
      setWorkerOptions(list);
    } catch { setWorkerOptions([]); }
  }, []);

  useEffect(() => { loadUsers(); loadCustomRoles(); loadWorkerOptions(); }, [loadUsers, loadCustomRoles, loadWorkerOptions]);

  const searchedUsers = userSearch
    ? users.filter(u => `${u.firstName} ${u.lastName} ${u.email} ${u.userName}`.toLowerCase().includes(userSearch.toLowerCase()))
    : users;
  const statusCounts = {
    all: searchedUsers.length,
    active: searchedUsers.filter(u => accountStatus(u) === 'active').length,
    pending_activation: searchedUsers.filter(u => accountStatus(u) === 'pending_activation').length,
    inactive: searchedUsers.filter(u => accountStatus(u) === 'inactive').length,
  };
  const filteredUsers = statusFilter === 'all'
    ? searchedUsers
    : searchedUsers.filter(u => accountStatus(u) === statusFilter);

  // ── User handlers ──
  const toggleUserActive = async (user) => {
    const newActive = user.active === false ? true : false;
    setUserSavingId(user.id);
    try {
      await putUser(user.id, { ...buildPayload(user), Active: newActive });
      setUsers(prev => prev.map(u => u.id===user.id ? {...u, active:newActive} : u));
      showSnack(newActive ? 'Përdoruesi u aktivizua.' : 'Llogaria u çaktivizua.');
    } catch (e) { showSnack(e?.message || 'Gabim gjatë ndryshimit të statusit.', 'error'); }
    finally { setUserSavingId(null); }
  };

  /**
   * Aktivizo të gjitha llogaritë joaktive të listës së dukshme.
   *
   * Klienti pati tetë llogari të fikura: një nga një do të thoshte tetë klikime
   * dhe tetë dritare. Vepron mbi ATË që sheh — një kërkim aktiv e ngushton.
   */
  const activateAllInactive = async () => {
    const targets = filteredUsers.filter(u => u.active === false);
    if (!targets.length) return;
    setBulkActivating(true);
    let ok = 0; const failed = [];
    for (const u of targets) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await putUser(u.id, { ...buildPayload(u), Active: true });
        ok++;
      } catch (e) { failed.push(`${u.email}: ${e?.message || 'gabim'}`); }
    }
    setUsers(prev => prev.map(u => (targets.some(t => t.id === u.id) && !failed.some(f => f.startsWith(u.email)))
      ? { ...u, active: true } : u));
    setBulkActivating(false);
    showSnack(
      `U aktivizuan ${ok} llogari${failed.length ? ` · ${failed.length} dështuan: ${failed.slice(0, 2).join(' · ')}` : ''}.`,
      failed.length ? (ok ? 'warning' : 'error') : 'success',
    );
  };

  const openDetails = (user) => {
    const perms = user.permissions || ROLE_DEFAULT_PERMS[user.role] || ROLE_DEFAULT_PERMS.employee;
    setDetailsDialog({ open:true, user:{ ...user, permissions:{ ...perms } } });
  };
  const closeDetails = () => setDetailsDialog({ open:false, user:null });

  const saveDetails = () => {
    if (!detailsDialog.user) return;
    const uid = detailsDialog.user.id;
    if (!uid) { showSnack('Gabim: ID mungon.', 'error'); return; }
    setDetailsSaving(true);
    putUser(uid, buildPayload(detailsDialog.user)).then(() => {
      setUsers(prev => prev.map(u => u.id===uid ? {...u, ...detailsDialog.user, originalRole: detailsDialog.user.role} : u));
      showSnack('Të dhënat u ruajtën.'); closeDetails();
    }).catch((e)=>showSnack(e?.message || 'Gabim.','error')).finally(()=>setDetailsSaving(false));
  };

  const sendAccessLink = async () => {
    if (!detailsDialog.user?.id) return;
    setSendingAccessLink(true);
    try {
      const res = await sendInviteEmail(detailsDialog.user.id, { now: true });
      showInviteResult(res, '', detailsDialog.user);
    }
    catch (e) { showSnack(e?.message || 'Dërgimi i ftesës dështoi.', 'error'); }
    finally { setSendingAccessLink(false); }
  };

  // ── Email change handlers ──
  const openEmailChange  = (user) => { setEmailChangeDialog({ open:true, user }); setEmailChangeData({ newEmail:'', confirmEmail:'' }); setEmailCheckResult(null); };
  const closeEmailChange = () => { setEmailChangeDialog({ open:false, user:null }); setEmailChangeData({ newEmail:'', confirmEmail:'' }); setEmailCheckResult(null); };

  const checkEmailAvailability = async (email, userId) => {
    if (!isValidEmail(email)) { setEmailCheckResult('invalid'); return; }
    if (email.toLowerCase().trim() === emailChangeDialog.user?.email?.toLowerCase()) { setEmailCheckResult('same'); return; }
    setEmailCheckResult('checking');
    try {
      const r = await authFetch(`/api/users/check-email?email=${encodeURIComponent(email)}&excludeId=${userId}`).then(x=>x.json());
      setEmailCheckResult(r.available ? 'available' : 'taken');
    } catch { setEmailCheckResult(null); }
  };

  const saveEmailChange = async () => {
    const { user }            = emailChangeDialog;
    const { newEmail, confirmEmail } = emailChangeData;
    if (!isValidEmail(newEmail)) { showSnack('Formati i emailit është i pavlefshëm.', 'error'); return; }
    if (newEmail.toLowerCase().trim() !== confirmEmail.toLowerCase().trim()) { showSnack('Email-et nuk përputhen.', 'error'); return; }
    if (newEmail.toLowerCase().trim() === user.email?.toLowerCase()) { showSnack('Emaili i ri duhet të jetë i ndryshëm.', 'error'); return; }
    setEmailChangeSaving(true);
    try {
      const r = await authFetch(`/api/users/${user.id}/change-email`, {
        method:'PUT', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ newEmail }),
      }).then(x=>x.json());
      if (r.success) {
        setUsers(prev => prev.map(u => u.id===user.id ? {...u, email:r.email} : u));
        if (detailsDialog.open && detailsDialog.user?.id===user.id) setDetailsDialog(p=>({...p, user:{...p.user, email:r.email}}));
        showSnack('Emaili u ndryshua me sukses.'); closeEmailChange();
      } else showSnack(r.error||'Gabim gjatë ndryshimit.','error');
    } catch { showSnack('Gabim i papritur.','error'); }
    finally { setEmailChangeSaving(false); }
  };

  const handleCreateUser = () => {
    if (!newUser.firstName?.trim() || !newUser.lastName?.trim() || !newUser.email?.trim()) {
      showSnack('Emri, mbiemri dhe emaili janë të detyrueshëm.', 'error'); return;
    }
    const prepared = { ...newUser, userName: newUser.userName?.trim() || newUser.email.split('@')[0] };
    setCreatingUser(true);
    apiService().post('users', buildPayload(prepared)).then(async r => {
      if (r?.success) {
        const createdId = r?.data?.Id || r?.data?.id;
        const created   = { ...prepared, id: createdId };
        if (newUser.sendInvite && createdId) {
          try {
            const res = await sendInviteEmail(createdId);
            showInviteResult(res, 'Përdoruesi u shtua. ');
          }
          catch (e) { showSnack(`Përdoruesi u shtua, por ftesa dështoi: ${e?.message || 'gabim'}.`, 'warning'); }
        } else showSnack('Përdoruesi u shtua me sukses.');
        setNewUser(BLANK_USER); setAddUserDialog(false); loadUsers(); openDetails(created);
      } else showSnack('Gabim gjatë shtimit.','error');
    }).catch((e)=>showSnack(e?.message || 'Gabim.','error')).finally(()=>setCreatingUser(false));
  };

  // ── Custom role handlers ──
  const openCustomRoleDialog = (role=null) => {
    setCustomRoleDialog({
      open:true,
      role: role ? {...role} : { ...BLANK_CUSTOM_ROLE, permissions:{ ...ROLE_DEFAULT_PERMS.employee } },
    });
  };
  const closeCustomRoleDialog = () => setCustomRoleDialog({ open:false, role:null });

  const saveCustomRole = async () => {
    const r = customRoleDialog.role;
    if (!r?.name?.trim()) { showSnack('Emri i rolit kërkohet.', 'error'); return; }
    setCustomRoleSaving(true);
    try {
      // Lejet e detajuara (varg) nuk mbishkruhen nga ky ekran: ruhen ashtu siç janë.
      const payload = { name:r.name.trim(), description:r.description||'', color:r.color||'#7C3AED', scope:r.scope||'company', permissions:r.permissions||{} };
      let res;
      if (r.id) {
        res = await authFetch(`/api/customRoles/${r.id}`, { method:'PUT', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload) }).then(x=>x.json());
      } else {
        res = await authFetch('/api/customRoles', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(payload) }).then(x=>x.json());
      }
      if (res?.success) { showSnack(r.id ? 'Roli u përditësua.' : 'Roli u krijua.'); loadCustomRoles(); closeCustomRoleDialog(); }
      else showSnack(res?.error||'Gabim gjatë ruajtjes.','error');
    } catch { showSnack('Gabim.','error'); }
    finally { setCustomRoleSaving(false); }
  };

  const deleteCustomRole = async (id) => {
    // `window.confirm` nuk tregon dot ÇFARË po fshihet: "Ky rol do të fshihet"
    // është e njëjta fjali për çdo rol, ndaj një klikim te rreshti i gabuar
    // konfirmohej njësoj lehtë si ai i saktë.
    setCustomRoleSaving(true);
    try {
      const r = await authFetch(`/api/customRoles/${id}`, { method:'DELETE' }).then(x=>x.json());
      if (r?.success) { setRoleToDelete(null); showSnack('Roli u fshi.'); loadCustomRoles(); }
      else showSnack(r?.error || 'Fshirja nuk u krye.', 'error');
    } catch { showSnack('Fshirja nuk u krye.', 'error'); }
    setCustomRoleSaving(false);
  };

  // Serveri vendos: s'ka hyrë kurrë dhe s'ka lënë gjurmë. Pyetet para dialogut,
  // që admini ta shohë arsyen menjëherë në vend të një «Fshi» që pastaj refuzohet.
  const askDeleteUser = async (user) => {
    setCheckingDeleteId(user.id);
    try {
      const r = await authFetch(`/api/users/${user.id}/deletion-check`).then(x => x.json());
      if (r?.success && r.data?.canDelete) setUserToDelete(user);
      else showSnack(r?.data?.reason || r?.error || 'Ky përdorues nuk mund të fshihet.', 'warning');
    } catch { showSnack('Kontrolli nuk u krye. Provo sërish.', 'error'); }
    setCheckingDeleteId(null);
  };

  const deleteUser = async () => {
    const user = userToDelete;
    if (!user) return;
    setUserDeleting(true);
    try {
      const r = await authFetch(`/api/users/${user.id}`, { method: 'DELETE' }).then(x => x.json());
      if (r?.success) {
        setUsers(prev => prev.filter(u => u.id !== user.id));
        showSnack('Përdoruesi u fshi.');
        setUserToDelete(null);
      } else {
        showSnack(r?.error || 'Fshirja nuk u krye.', 'error');
        setUserToDelete(null);
      }
    } catch { showSnack('Fshirja nuk u krye.', 'error'); }
    setUserDeleting(false);
  };

  const renderUserActions = (user, mobile = false) => (
    <Stack direction="row" alignItems="center" justifyContent={mobile ? 'space-between' : 'flex-end'} spacing={0.5} sx={{ minWidth:0 }}>
      <Button size="small" variant={mobile ? 'text' : 'outlined'} startIcon={<EditIcon sx={{ fontSize:16 }}/>} onClick={()=>openDetails(user)}
        sx={{ textTransform:'none', fontWeight:700, minHeight:40, borderRadius:'8px', px:mobile?1:1.5 }}>
        Detaje
      </Button>
      <Tooltip title="Ndrysho emailin">
        <Button size="small" aria-label={`Ndrysho emailin e ${user.firstName || user.userName}`} onClick={()=>openEmailChange(user)}
          startIcon={mobile ? <AlternateEmailIcon sx={{ fontSize:17 }}/> : undefined}
          sx={{ textTransform:'none', fontWeight:650, minHeight:40, minWidth:mobile?60:40, px:mobile?1:0.5 }}>
          {mobile ? 'Email' : <AlternateEmailIcon sx={{ fontSize:19 }}/>}</Button>
      </Tooltip>
      <Tooltip title={user.active===false ? 'Aktivizo llogarinë' : 'Çaktivizo llogarinë'}>
        <span>
          <Button size="small" color={user.active===false?'success':'error'} disabled={userSavingId===user.id}
            aria-label={`${user.active===false?'Aktivizo':'Çaktivizo'} llogarinë e ${user.firstName || user.userName}`}
            onClick={()=>user.active===false ? toggleUserActive(user) : setUserToDeactivate(user)}
            startIcon={mobile && userSavingId!==user.id ? (user.active===false?<CheckCircleIcon sx={{ fontSize:17 }}/>:<BlockIcon sx={{ fontSize:17 }}/>) : undefined}
            sx={{ textTransform:'none', fontWeight:650, minHeight:40, minWidth:mobile?76:40, px:mobile?1:0.5 }}>
            {userSavingId===user.id ? <CircularProgress size={17}/> : mobile ? (user.active===false?'Aktivizo':'Çaktivizo') : user.active===false ? <CheckCircleIcon sx={{ fontSize:19 }}/> : <BlockIcon sx={{ fontSize:19 }}/>}
          </Button>
        </span>
      </Tooltip>
      {!user.lastLoginAt && (
        <Tooltip title="Ridërgo ftesën me email">
          <span>
            <Button size="small" disabled={resendingId===user.id}
              aria-label={`Ridërgo ftesën te ${user.firstName || user.userName}`}
              onClick={()=>resendInvite(user)}
              startIcon={mobile && resendingId!==user.id ? <ForwardToInboxIcon sx={{ fontSize:17 }}/> : undefined}
              sx={{ textTransform:'none', fontWeight:650, minHeight:40, minWidth:mobile?76:40, px:mobile?1:0.5 }}>
              {resendingId===user.id ? <CircularProgress size={17}/> : mobile ? 'Ftesa' : <ForwardToInboxIcon sx={{ fontSize:19 }}/>}
            </Button>
          </span>
        </Tooltip>
      )}
      {!user.lastLoginAt && (
        <Tooltip title="Fshi përdoruesin — vetëm kur s'ka hyrë kurrë dhe s'ka të dhëna">
          <span>
            <Button size="small" color="error" disabled={checkingDeleteId===user.id}
              aria-label={`Fshi përdoruesin ${user.firstName || user.userName}`}
              onClick={()=>askDeleteUser(user)}
              startIcon={mobile && checkingDeleteId!==user.id ? <DeleteIcon sx={{ fontSize:17 }}/> : undefined}
              sx={{ textTransform:'none', fontWeight:650, minHeight:40, minWidth:mobile?60:40, px:mobile?1:0.5 }}>
              {checkingDeleteId===user.id ? <CircularProgress size={17}/> : mobile ? 'Fshi' : <DeleteIcon sx={{ fontSize:19 }}/>}
            </Button>
          </span>
        </Tooltip>
      )}
    </Stack>
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Përmbledhja është edhe filtër: numrat ndjekin kërkimin aktiv. */}
      <Box sx={{ display:'grid', gridTemplateColumns:{ xs:'repeat(2, minmax(0, 1fr))', sm:'repeat(4, minmax(0, 1fr))' }, gap:1, mb:2 }}>
        {[
          { key:'all', label:'Gjithsej', icon:GroupIcon, color:'#256878' },
          { key:'active', label:'Aktive', icon:CheckCircleIcon, color:'#15803D' },
          { key:'pending_activation', label:'Në pritje', icon:HourglassEmptyIcon, color:'#B45309' },
          { key:'inactive', label:'Joaktive', icon:PersonOffIcon, color:'#64748B' },
        ].map(({ key, label, icon: Icon, color }) => (
          <Box key={key} component="button" type="button" aria-pressed={statusFilter===key}
            onClick={() => setStatusFilter(key)}
            sx={{ textAlign:'left', cursor:'pointer', font:'inherit', minWidth:0, p:1.25,
              border:'1px solid', borderColor:statusFilter===key?'#3E8FA3':'#E2E8F0', borderRadius:'10px',
              bgcolor:statusFilter===key?'#F1FAFC':'#FFFFFF', boxShadow:statusFilter===key?'0 0 0 2px rgba(62,143,163,0.1)':'none',
              transition:'border-color .15s, background .15s',
              '&:hover':{ borderColor:'#3E8FA3', bgcolor:'#F7FBFC' },
              '&:focus-visible':{ outline:'3px solid #9BD5E3', outlineOffset:2 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography sx={{ fontSize:{ xs:12, sm:13 }, fontWeight:650, color:'#64748B' }}>{label}</Typography>
              <Icon sx={{ fontSize:18, color }} />
            </Stack>
            <Typography sx={{ mt:0.75, fontSize:{ xs:22, sm:24 }, lineHeight:1, fontWeight:750, color:'#0F172A', fontVariantNumeric:'tabular-nums' }}>
              {statusCounts[key]}
            </Typography>
          </Box>
        ))}
      </Box>

      <Stack direction={{ xs:'column', sm:'row' }} alignItems={{ xs:'stretch', sm:'center' }} justifyContent="space-between"
        spacing={1.5} sx={{ mb:2 }}>
        <Box>
          <Typography sx={{ fontSize:18, fontWeight:750, color:'#0F172A', lineHeight:1.3 }}>Llogaritë e përdoruesve</Typography>
          <Typography sx={{ fontSize:13, color:'#64748B', mt:0.4 }}>
            Kërko, ndrysho qasjet dhe menaxho statusin e secilës llogari.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAddAltIcon sx={{ fontSize:18 }}/>} onClick={()=>{ setNewUser(BLANK_USER); setAddUserDialog(true); }}
          sx={{ borderRadius:'8px', textTransform:'none', fontWeight:700, minHeight:42, px:2.25, alignSelf:{ xs:'stretch', sm:'center' }, flexShrink:0, boxShadow:'none' }}>
          Shto përdorues
        </Button>
      </Stack>

      <Box sx={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:1, mb:1.5 }}>
        <TextField size="small" placeholder="Kërko sipas emrit ose emailit"
          value={userSearch} onChange={e=>setUserSearch(e.target.value)}
          sx={{ flex:'1 1 240px', minWidth:0, '& .MuiOutlinedInput-root':{ bgcolor:'#FFFFFF', borderRadius:'8px', minHeight:42 } }}
          InputProps={{ startAdornment:<InputAdornment position="start"><SearchIcon sx={{ fontSize:19, color:'#64748B' }}/></InputAdornment> }}
        />
        <Typography sx={{ fontSize:12.5, color:'#64748B', flexShrink:0, whiteSpace:'nowrap' }}>
          {filteredUsers.length} nga {searchedUsers.length} llogari{userSearch ? ' që përputhen me kërkimin' : ''}
        </Typography>
        {filteredUsers.filter(u=>u.active===false).length > 1 && (
          <Button variant="outlined" size="small" color="success" disabled={bulkActivating}
            startIcon={bulkActivating ? <CircularProgress size={15}/> : <CheckCircleIcon sx={{ fontSize:17 }}/>} onClick={activateAllInactive}
            sx={{ borderRadius:'8px', textTransform:'none', fontWeight:650, minHeight:42, whiteSpace:'nowrap' }}>
            Aktivizo {filteredUsers.filter(u=>u.active===false).length} joaktivët
          </Button>
        )}
      </Box>

      <Card variant="outlined" sx={{ borderRadius:'12px', overflow:'hidden', borderColor:'#E2E8F0', boxShadow:'0 4px 16px rgba(15,23,42,0.03)', mb:3 }}>
        {usersLoading ? (
          <Stack alignItems="center" spacing={1} sx={{ py:6 }}><CircularProgress size={24}/><Typography variant="body2" color="text.secondary">Duke ngarkuar përdoruesit…</Typography></Stack>
        ) : filteredUsers.length===0 ? (
          <Stack alignItems="center" spacing={1} sx={{ px:2, py:6, textAlign:'center' }}>
            <PersonOffIcon sx={{ fontSize:32, color:'#94A3B8' }}/>
            <Typography sx={{ fontWeight:700, color:'#334155' }}>Nuk u gjet asnjë llogari</Typography>
            <Typography variant="body2" color="text.secondary">
              {userSearch ? 'Provo një emër ose email tjetër.' : statusFilter!=='all' ? 'Nuk ka llogari me këtë status.' : 'Shto përdoruesin e parë për të filluar.'}
            </Typography>
          </Stack>
        ) : (
          <>
            <Box sx={{ display:{ xs:'none', md:'block' }, overflowX:'auto' }}>
              <Table size="small" sx={{ minWidth:720 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor:'#F8FAFC' }}>
                    <TableCell sx={{ fontWeight:750, fontSize:12, color:'#64748B', py:1.5, pl:2.5 }}>PËRDORUESI</TableCell>
                    <TableCell sx={{ fontWeight:750, fontSize:12, color:'#64748B', py:1.5 }}>ROLI</TableCell>
                    <TableCell sx={{ fontWeight:750, fontSize:12, color:'#64748B', py:1.5 }}>STATUSI</TableCell>
                    <TableCell align="right" sx={{ fontWeight:750, fontSize:12, color:'#64748B', py:1.5, pr:2.5 }}>VEPRIMET</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map(user => {
                    const st = accountStatusChip(user);
                    return (
                      <TableRow key={user.id||user.userName} hover sx={{ '&:last-child td':{ borderBottom:0 } }}>
                        <TableCell sx={{ py:1.5, pl:2.5 }}>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar sx={{ width:40, height:40, fontSize:13, fontWeight:700, bgcolor:user.active===false?'#94A3B8':'#3E8FA3' }}>
                              {initials(`${user.firstName} ${user.lastName}`)}
                            </Avatar>
                            <Box sx={{ minWidth:0 }}>
                              <Typography sx={{ fontSize:13.5, fontWeight:700, color:'#0F172A', lineHeight:1.35 }}>
                                {`${user.firstName} ${user.lastName}`.trim() || user.userName}
                              </Typography>
                              <Typography sx={{ fontSize:12, color:'#64748B', overflowWrap:'anywhere' }}>{user.email}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ py:1.5 }}>
                          <Chip size="small" label={ROLE_LABELS[user.role]||user.role} color={ROLE_COLORS[user.role]||'default'}
                            variant="outlined" sx={{ fontSize:11.5, height:27, fontWeight:700, borderRadius:'6px' }}/>
                        </TableCell>
                        <TableCell sx={{ py:1.5 }}>
                          <Chip size="small" label={st.label} color={st.color} variant={st.variant}
                            sx={{ fontSize:11.5, height:27, fontWeight:650, borderRadius:'6px' }}/>
                        </TableCell>
                        <TableCell align="right" sx={{ py:1, pr:2 }}>
                          {renderUserActions(user)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <Box sx={{ display:{ xs:'grid', md:'none' }, gap:0, p:0 }}>
              {filteredUsers.map(user => {
                const st = accountStatusChip(user);
                return (
                  <Box key={user.id||user.userName} sx={{ borderBottom:'1px solid #E2E8F0', p:1.5, minWidth:0, '&:last-child':{ borderBottom:0 } }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                      <Avatar sx={{ width:40, height:40, fontSize:13, fontWeight:700, bgcolor:user.active===false?'#94A3B8':'#3E8FA3' }}>
                        {initials(`${user.firstName} ${user.lastName}`)}
                      </Avatar>
                      <Box sx={{ minWidth:0, flex:1 }}>
                        <Typography sx={{ fontSize:14, fontWeight:750, color:'#0F172A', lineHeight:1.3 }}>
                          {`${user.firstName} ${user.lastName}`.trim() || user.userName}
                        </Typography>
                        <Typography sx={{ fontSize:12.5, color:'#64748B', overflowWrap:'anywhere' }}>{user.email}</Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt:1.5, mb:1.25 }}>
                      <Chip size="small" label={ROLE_LABELS[user.role]||user.role} color={ROLE_COLORS[user.role]||'default'} variant="outlined" sx={{ height:26, fontWeight:700 }}/>
                      <Chip size="small" label={st.label} color={st.color} variant={st.variant} sx={{ height:26, fontWeight:650 }}/>
                    </Stack>
                    <Divider sx={{ mb:0.75 }}/>
                    {renderUserActions(user, true)}
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Card>

      {/* ── Add User Dialog ── */}
      <Dialog open={addUserDialog} onClose={()=>{ setAddUserDialog(false); setNewUser(BLANK_USER); }} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:'12px' } }}>
        <Box sx={{ px:3, pt:2.5, pb:1.5, display:'flex', alignItems:'center', gap:1.5 }}>
          <Avatar sx={{ bgcolor:'primary.main', width:36, height:36 }}><PersonAddAltIcon sx={{ fontSize:18 }}/></Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Shto përdorues të ri</Typography>
            <Typography variant="caption" color="text.secondary">Plotëso të dhënat dhe cakto rolin</Typography>
          </Box>
        </Box>
        <Divider/>
        <DialogContent sx={{ pt:2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField size="small" fullWidth label="Emri *" value={newUser.firstName} onChange={e=>setNewUser(p=>({...p,firstName:e.target.value}))}/></Grid>
            <Grid item xs={6}><TextField size="small" fullWidth label="Mbiemri *" value={newUser.lastName} onChange={e=>setNewUser(p=>({...p,lastName:e.target.value}))}/></Grid>
            <Grid item xs={12}>
              <TextField size="small" fullWidth label="Adresa email *" type="email" value={newUser.email}
                onChange={e=>setNewUser(p=>({...p,email:e.target.value}))}
                InputProps={{ startAdornment:<InputAdornment position="start"><EmailIcon sx={{ fontSize:15, color:'text.disabled' }}/></InputAdornment> }}/>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select size="small" fullWidth label="Roli & privilegjet" value={newUser.role}
                onChange={e=>setNewUser(p=>({...p,role:e.target.value}))}>
                {ROLES_RBAC.map(r=>(
                  <MenuItem key={r.id} value={r.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor:r.color, flexShrink:0 }}/>
                      <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p:1.5, bgcolor:'grey.50', borderRadius:'8px', height:'100%', display:'flex', flexDirection:'column', justifyContent:'center' }}>
                <Typography variant="caption" fontWeight={700} sx={{ color:ROLES_RBAC.find(x=>x.id===newUser.role)?.color||'text.primary', display:'block', mb:0.25 }}>
                  {ROLES_RBAC.find(x=>x.id===newUser.role)?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight:1.4 }}>
                  {ROLES_RBAC.find(x=>x.id===newUser.role)?.desc}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p:1.5, border:'1px solid', borderColor:'divider', borderRadius:'8px', bgcolor:newUser.sendInvite?'primary.50':'transparent' }}>
                <FormControlLabel sx={{ m:0 }} control={<Checkbox size="small" checked={newUser.sendInvite} onChange={e=>setNewUser(p=>({...p,sendInvite:e.target.checked}))}/>}
                  label={<Box><Typography variant="body2" fontWeight={600}>Dërgo email-in e ftesës</Typography><Typography variant="caption" color="text.secondary">Përdoruesi merr një link për të vendosur vetë fjalëkalimin (vlen 72 orë)</Typography></Box>}/>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2.5, gap:1 }}>
          <Button onClick={()=>{ setAddUserDialog(false); setNewUser(BLANK_USER); }} variant="outlined" sx={{ borderRadius:'8px', textTransform:'none', flex:1 }}>Anulo</Button>
          <Button onClick={handleCreateUser} variant="contained" disabled={creatingUser}
            startIcon={creatingUser?<CircularProgress size={14} color="inherit"/>:<PersonAddAltIcon/>}
            sx={{ borderRadius:'8px', textTransform:'none', flex:2, fontWeight:600 }}>
            {creatingUser ? 'Duke shtuar...' : 'Shto përdoruesin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit User Dialog ── */}
      <Dialog open={detailsDialog.open} onClose={closeDetails} maxWidth="md" fullWidth PaperProps={{ sx:{ borderRadius:'12px' } }}>
        {detailsDialog.user && (()=>{
          const u = detailsDialog.user;
          const setU = (patch) => setDetailsDialog(p=>({...p, user:{...p.user,...patch}}));
          const setPermission = (mod, val) => setDetailsDialog(p=>({ ...p, user:{ ...p.user, permissions:{ ...(p.user.permissions||{}), [mod]:val } } }));
          const applyRoleDefaults = () => { const perms = ROLE_DEFAULT_PERMS[u.role]||ROLE_DEFAULT_PERMS.employee; setU({ permissions:{...perms} }); };
          const onRoleChange = (e) => { const newRole=e.target.value; setU({ role:newRole, permissions:{ ...ROLE_DEFAULT_PERMS[newRole]||ROLE_DEFAULT_PERMS.employee } }); };
          const roleInfo = ROLES_RBAC.find(r=>r.id===u.role);
          return (
            <>
              <Box sx={{ px:3, pt:2.5, pb:1.5, display:'flex', alignItems:'center', gap:1.5, borderBottom:'1px solid', borderColor:'divider' }}>
                <Avatar sx={{ width:46, height:46, fontSize:16, fontWeight:700, bgcolor: u.active===false?'grey.400':(roleInfo?.color||'#1976d2') }}>
                  {initials(`${u.firstName||''} ${u.lastName||''}`)}
                </Avatar>
                <Box sx={{ flex:1 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight:1.3 }}>
                    {`${u.firstName||''} ${u.lastName||''}`.trim()||u.userName}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                    <Tooltip title="Ndrysho emailin">
                      <Chip size="small" label="Ndrysho emailin" icon={<AlternateEmailIcon sx={{ fontSize:'12px !important' }}/>} clickable
                        onClick={()=>{ closeDetails(); openEmailChange(u); }}
                        sx={{ height:20, fontSize:10, fontWeight:600, bgcolor:'#EEF2FF', color:'#2563EB', border:'1px solid #BFDBFE', '& .MuiChip-icon':{ color:'#2563EB', ml:0.5 }, '&:hover':{ bgcolor:'#DBEAFE' } }}/>
                    </Tooltip>
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  {roleInfo && <Chip size="small" label={roleInfo.name} sx={{ fontSize:11, height:22, bgcolor:roleInfo.color+'18', color:roleInfo.color, border:`1px solid ${roleInfo.color}40`, fontWeight:600 }}/>}
                  {(() => { const st = accountStatusChip(u); return (<Chip size="small" label={st.label} color={st.color} variant={st.variant} sx={{ fontSize:11, height:22 }}/>); })()}
                </Stack>
              </Box>

              <DialogContent sx={{ pt:2.5, pb:1 }}>
                <Grid container spacing={3}>
                  {/* Left: basic info */}
                  <Grid item xs={12} md={5}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform:'uppercase', letterSpacing:0.5, display:'block', mb:1.5 }}>Informacioni bazë</Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}><TextField size="small" fullWidth label="Emri" value={u.firstName||''} onChange={e=>setU({firstName:e.target.value})}/></Grid>
                      <Grid item xs={6}><TextField size="small" fullWidth label="Mbiemri" value={u.lastName||''} onChange={e=>setU({lastName:e.target.value})}/></Grid>
                      <Grid item xs={6}><TextField size="small" fullWidth label="Telefoni" value={u.phone||''} onChange={e=>setU({phone:e.target.value})}/></Grid>
                      <Grid item xs={6}><TextField size="small" fullWidth label="Pozita / Titulli" value={u.jobTitle||''} onChange={e=>setU({jobTitle:e.target.value})}/></Grid>
                      <Grid item xs={12}>
                        <TextField select size="small" fullWidth label="Punëtori i lidhur"
                          value={u.worker_id ?? ''}
                          onChange={e=>setU({ worker_id: e.target.value === '' ? null : Number(e.target.value) })}
                          helperText="Lidh llogarinë me dosjen e punëtorit: prej saj vijnë lejet e pozitës, orari dhe pushimet.">
                          <MenuItem value=""><em>Pa lidhje</em></MenuItem>
                          {workerOptions.map(w => (
                            <MenuItem key={w.id} value={w.id} sx={{ fontSize:13 }}>
                              {w.name || `#${w.id}`}{w.email ? ` · ${w.email}` : ''}{w.active ? '' : ' · joaktiv'}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}><TextField size="small" fullWidth label="Shënime" multiline minRows={3} value={u.notes||''} onChange={e=>setU({notes:e.target.value})}/></Grid>
                    </Grid>
                    <Divider sx={{ my:2 }}/>
                    <Box sx={{ p:1.5, borderRadius:'8px', border:'1px solid', borderColor:u.active===false?'error.200':'success.200', bgcolor:u.active===false?'#FFF5F5':'#F0FDF4' }}>
                      <FormControlLabel sx={{ m:0, width:'100%' }}
                        control={<Switch size="small" checked={u.active!==false} onChange={e=>setU({active:e.target.checked})} color={u.active===false?'default':'success'}/>}
                        label={<Box sx={{ ml:0.5 }}>
                          <Typography variant="body2" fontWeight={600}>{u.active===false?'Llogari joaktive':'Llogari aktive'}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.active===false?'Ky përdorues nuk mund të hyjë':accountStatus(u)==='pending_activation'?`Hyrja hapet më ${formatDMY(u.activationDate)} (data e aktivizimit te profili i punëtorit)`:'Ka qasje sipas privilegjeve'}</Typography>
                        </Box>}/>
                    </Box>
                  </Grid>

                  {/* Right: role + permissions */}
                  <Grid item xs={12} md={7}>
                    {/* Flow hint — sqaron lidhjen mes 3 tabeve të hub-it */}
                    <Box sx={{ display:'flex', gap:0.75, alignItems:'flex-start', p:1, mb:1.5, borderRadius:'8px', bgcolor:'grey.50', border:'1px solid', borderColor:'divider' }}>
                      <InfoOutlinedIcon sx={{ fontSize:15, color:'primary.main', mt:'1px', flexShrink:0 }}/>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight:1.45 }}>
                        <strong>Roli</strong> jep qasjen bazë (shabllonet menaxhohen te tab-i <em>Rolet & Privilegjet</em>).{' '}
                        <strong>Pozita</strong> (tab-i <em>Pozitat & Qasjet</em>) shton leje sipas punës.{' '}
                        Matrica poshtë mbivendos leje <strong>vetëm për këtë përdorues</strong>.
                      </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb:1 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform:'uppercase', letterSpacing:0.5 }}>Roli bazë</Typography>
                    </Stack>
                    <TextField select size="small" fullWidth value={u.role||'staff'} onChange={onRoleChange} sx={{ mb:2 }}>
                      {ROLES_RBAC.map(r=>(
                        <MenuItem key={r.id} value={r.id}>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{ width:10, height:10, borderRadius:'50%', bgcolor:r.color, flexShrink:0 }}/>
                            <Box><Typography variant="body2" fontWeight={600} sx={{ lineHeight:1.2 }}>{r.name}</Typography><Typography variant="caption" color="text.secondary">{r.desc}</Typography></Box>
                          </Stack>
                        </MenuItem>
                      ))}
                    </TextField>

                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb:1 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform:'uppercase', letterSpacing:0.5 }}>Privilegjet sipas modulit</Typography>
                      <Tooltip title="Rivendos privilegjet bazuar në rolin e zgjedhur">
                        <Button size="small" variant="text" onClick={applyRoleDefaults}
                          sx={{ textTransform:'none', fontSize:11, color:'text.secondary', py:0, minWidth:0 }}>
                          ↺ Rivendos nga roli
                        </Button>
                      </Tooltip>
                    </Stack>

                    <Box sx={{ mb:1.5, p:1.25, bgcolor:'grey.50', borderRadius:'8px', border:'1px solid', borderColor:'divider' }}>
                        <Stack direction="row" alignItems="center" sx={{ mb:0.75 }}>
                          <Typography variant="caption" fontWeight={700} color="text.secondary">
                            <WorkspacePremiumIcon sx={{ fontSize:12, mr:0.5, verticalAlign:'middle', color:'primary.main' }}/>
                            Rol i personalizuar
                          </Typography>
                          <Box sx={{ flex:1 }}/>
                          {/* Hyrja që mungonte. Dialogu i roleve — emri, ngjyra dhe
                              matrica e lejeve për çdo modul — ekzistonte i plotë, por
                              `openCustomRoleDialog` nuk thirrej askund: as këtu, as te
                              Company.js ku i njëjti bllok është kopjuar. Pra rolet
                              mund të CAKTOHESHIN, kurrë të krijoheshin apo fshiheshin,
                              dhe tri rrugë të backend-it nuk arriheshin dot nga asnjë ekran. */}
                          <Button size="small" onClick={()=>setManageRoles(m=>!m)}
                            sx={{ textTransform:'none', fontSize:11, minWidth:0, py:0, color:'text.secondary' }}>
                            {manageRoles ? 'U krye' : 'Menaxho'}
                          </Button>
                          <Button size="small" startIcon={<AddIcon sx={{ fontSize:13 }}/>}
                            onClick={()=>openCustomRoleDialog(null)}
                            sx={{ textTransform:'none', fontSize:11, minWidth:0, py:0, ml:0.5 }}>
                            Rol i ri
                          </Button>
                        </Stack>

                        {/* Caktimi dhe menaxhimi mbahen të ndara me qëllim: po t'i
                            vinim veprimet mbi vetë çipin, "cakto këtë rol" dhe "fshi
                            këtë rol" do të ishin i njëjti objektiv, një piksel larg. */}
                        {manageRoles && (
                          <Stack spacing={0.5} sx={{ mb:1 }}>
                            {customRoles.length === 0 && (
                              <Typography variant="caption" color="text.disabled">
                                Ende asnjë rol i personalizuar.
                              </Typography>
                            )}
                            {customRoles.map(cr=>(
                              <Stack key={cr.id} direction="row" alignItems="center" spacing={1}
                                sx={{ px:1, py:0.5, bgcolor:'#fff', borderRadius:'6px', border:'1px solid', borderColor:'divider' }}>
                                <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor:cr.color||'#7C3AED', flexShrink:0 }}/>
                                <Typography sx={{ fontSize:12, fontWeight:600, flex:1 }}>{cr.name}</Typography>
                                <Tooltip title="Ndrysho"><IconButton size="small" onClick={()=>openCustomRoleDialog(cr)} sx={{ width:24, height:24 }}>
                                  <EditIcon sx={{ fontSize:13 }}/></IconButton></Tooltip>
                                <Tooltip title="Fshi"><IconButton size="small" onClick={()=>setRoleToDelete(cr)} sx={{ width:24, height:24 }}>
                                  <DeleteIcon sx={{ fontSize:13 }}/></IconButton></Tooltip>
                              </Stack>
                            ))}
                          </Stack>
                        )}
                        {/* Selecting a custom role sets custom_role_id, which the backend
                            enforces (services/effectivePermissions). "Asnjë" clears it →
                            the user falls back to their base role above. */}
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          <Chip size="small" label="Asnjë" clickable
                            onClick={()=>setDetailsDialog(p=>({...p, user:{...p.user, customRoleId:null}}))}
                            variant={u.customRoleId ? 'outlined' : 'filled'}
                            color={u.customRoleId ? 'default' : 'primary'}
                            sx={{ fontSize:11, height:24, fontWeight:600 }}/>
                          {customRoles.map(cr=>{
                            const selected = u.customRoleId === cr.id;
                            return (
                              <Tooltip key={cr.id} title={cr.description||cr.name} placement="top">
                                <Chip size="small" label={cr.name} clickable
                                  onClick={()=>setDetailsDialog(p=>({...p, user:{...p.user, customRoleId:cr.id}}))}
                                  sx={{ fontSize:11, height:24, fontWeight:600,
                                    bgcolor:(cr.color||'#7C3AED')+(selected?'30':'15'), color:cr.color||'#7C3AED',
                                    border:`${selected?2:1}px solid ${(cr.color||'#7C3AED')}${selected?'':'40'}`,
                                    '&:hover':{ bgcolor:(cr.color||'#7C3AED')+'30' } }}/>
                              </Tooltip>
                            );
                          })}
                        </Stack>
                        {u.customRoleId && (
                          <Typography variant="caption" color="primary.main" sx={{ display:'block', mt:0.75, fontWeight:600 }}>
                            Lejet vijnë nga roli i personalizuar (matrica poshtë s'zbatohet për këtë përdorues).
                          </Typography>
                        )}
                      </Box>

                    <PermissionEditor permissions={u.permissions} onChange={setPermission}/>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions sx={{ px:3, pb:2.5, pt:2, borderTop:'1px solid', borderColor:'divider', gap:1 }}>
                <Button onClick={closeDetails} variant="outlined" sx={{ borderRadius:'8px', textTransform:'none' }}>Anulo</Button>
                <Box sx={{ flex:1 }}/>
                <Button onClick={sendAccessLink} variant="outlined" color="secondary" disabled={sendingAccessLink||detailsSaving}
                  startIcon={sendingAccessLink?<CircularProgress size={14} color="inherit"/>:<LockResetIcon/>}
                  sx={{ borderRadius:'8px', textTransform:'none' }}>
                  {sendingAccessLink ? 'Duke dërguar...' : 'Dërgo link aksesi'}
                </Button>
                <Button onClick={saveDetails} variant="contained" disabled={detailsSaving}
                  startIcon={detailsSaving?<CircularProgress size={14} color="inherit"/>:<SaveIcon/>}
                  sx={{ borderRadius:'8px', textTransform:'none', fontWeight:600 }}>
                  {detailsSaving ? 'Duke ruajtur...' : 'Ruaj ndryshimet'}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ── Email Change Dialog ── */}
      <Dialog open={emailChangeDialog.open} onClose={emailChangeSaving?undefined:closeEmailChange} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:'12px', overflow:'hidden' } }}>
        {emailChangeDialog.user && (()=>{
          const u = emailChangeDialog.user;
          const { newEmail, confirmEmail } = emailChangeData;
          const emailValid  = isValidEmail(newEmail);
          const emailsSame  = newEmail.toLowerCase().trim()===u.email?.toLowerCase();
          const emailsMatch = newEmail.toLowerCase().trim()===confirmEmail.toLowerCase().trim()&&confirmEmail.length>0;
          const canSubmit   = emailValid&&!emailsSame&&emailsMatch&&emailCheckResult==='available'&&!emailChangeSaving;
          const statusColor = { available:'#16A34A', taken:'#DC2626', invalid:'#D97706', same:'#D97706', checking:'#2563EB' };
          const statusIcon  = { available:<CheckCircleOutlineIcon sx={{ fontSize:16 }}/>, taken:<ErrorOutlineIcon sx={{ fontSize:16 }}/>, invalid:<ErrorOutlineIcon sx={{ fontSize:16 }}/>, same:<ErrorOutlineIcon sx={{ fontSize:16 }}/>, checking:<HourglassEmptyIcon sx={{ fontSize:16 }}/> };
          const statusText  = { available:'Email i disponueshëm', taken:'Email i regjistruar tashmë', invalid:'Format i pavlefshëm', same:'Emaili aktual i përdoruesit', checking:'Duke kontrolluar...' };
          return (
            <>
              <Box sx={{ height:4, background:'linear-gradient(90deg, #3E8FA3 0%, #5AACBE 100%)' }}/>
              <Box sx={{ px:3, pt:2.5, pb:1.5, display:'flex', alignItems:'center', gap:1.5, borderBottom:'1px solid', borderColor:'divider' }}>
                <Avatar sx={{ bgcolor:'#EEF2FF', width:44, height:44 }}><AlternateEmailIcon sx={{ color:'#2563EB', fontSize:22 }}/></Avatar>
                <Box sx={{ flex:1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Ndrysho emailin</Typography>
                  <Typography variant="caption" color="text.secondary">{`${u.firstName||''} ${u.lastName||''}`.trim()||u.email}</Typography>
                </Box>
              </Box>
              <DialogContent sx={{ pt:2.5, pb:1 }}>
                <Box sx={{ p:1.5, bgcolor:'grey.50', borderRadius:'8px', border:'1px solid', borderColor:'divider', mb:2.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display:'block', mb:0.5 }}>Email aktual</Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EmailIcon sx={{ fontSize:16, color:'text.disabled' }}/>
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily:'monospace' }}>{u.email}</Typography>
                  </Stack>
                </Box>
                <Alert severity="warning" sx={{ mb:2.5, fontSize:'0.8rem', py:0.75 }}>
                  Pas ndryshimit, përdoruesi duhet të hyjë me emailin e ri.
                </Alert>
                <Stack spacing={0.75} sx={{ mb:2 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">Email i ri *</Typography>
                  <TextField size="small" fullWidth placeholder="shembull@kompania.com" type="email"
                    value={newEmail}
                    onChange={e=>{ setEmailChangeData(p=>({...p,newEmail:e.target.value})); setEmailCheckResult(null); }}
                    onBlur={e=>checkEmailAvailability(e.target.value, u.id)}
                    error={emailCheckResult==='taken'||emailCheckResult==='invalid'||emailCheckResult==='same'}
                    InputProps={{
                      startAdornment:<InputAdornment position="start"><AlternateEmailIcon sx={{ fontSize:16, color:'text.disabled' }}/></InputAdornment>,
                      endAdornment: emailCheckResult ? (
                        <InputAdornment position="end">
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color:statusColor[emailCheckResult]||'text.secondary' }}>
                            {statusIcon[emailCheckResult]}
                            <Typography variant="caption" fontWeight={600} sx={{ color:'inherit', whiteSpace:'nowrap' }}>{statusText[emailCheckResult]}</Typography>
                          </Stack>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{ '& .MuiOutlinedInput-root': emailCheckResult==='available'?{'& fieldset':{ borderColor:'#16A34A' }}:{} }}/>
                </Stack>
                <Stack spacing={0.75}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">Konfirmo emailin e ri *</Typography>
                  <TextField size="small" fullWidth placeholder="Rishkruaj emailin e ri" value={confirmEmail}
                    onChange={e=>setEmailChangeData(p=>({...p,confirmEmail:e.target.value}))}
                    error={confirmEmail.length>0&&!emailsMatch}
                    helperText={confirmEmail.length>0&&!emailsMatch?'Email-et nuk përputhen':undefined}
                    InputProps={{ startAdornment:<InputAdornment position="start"><AlternateEmailIcon sx={{ fontSize:16, color:'text.disabled' }}/></InputAdornment> }}/>
                </Stack>
              </DialogContent>
              <DialogActions sx={{ px:3, pb:2.5, pt:2, borderTop:'1px solid', borderColor:'divider', gap:1 }}>
                <Button onClick={closeEmailChange} variant="outlined" disabled={emailChangeSaving} sx={{ borderRadius:'8px', textTransform:'none' }}>Anulo</Button>
                <Button onClick={saveEmailChange} variant="contained" disabled={!canSubmit}
                  startIcon={emailChangeSaving?<CircularProgress size={14} color="inherit"/>:<SaveIcon/>}
                  sx={{ borderRadius:'8px', textTransform:'none', fontWeight:600, flex:1 }}>
                  {emailChangeSaving ? 'Duke ndryshuar...' : 'Ndrysho emailin'}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ── Custom Role Dialog ── */}
      <ConfirmDialog
        open={Boolean(userToDeactivate)}
        title="Çaktivizo llogarinë?"
        message="Kjo llogari nuk do të mund të hyjë më në sistem. Seanca e hapur mbyllet me kërkesën e parë."
        details={userToDeactivate ? `${userToDeactivate.firstName} ${userToDeactivate.lastName} · ${userToDeactivate.email}` : ''}
        confirmLabel="Çaktivizo"
        onClose={() => setUserToDeactivate(null)}
        onConfirm={() => { const u = userToDeactivate; setUserToDeactivate(null); toggleUserActive(u); }}
      />

      <ConfirmDialog
        open={Boolean(userToDelete)}
        title="Fshi përdoruesin"
        message="Llogaria nuk është përdorur kurrë dhe s'ka të dhëna në histori, ndaj fshihet plotësisht. Linku i ftesës nuk do të punojë më. Ky veprim nuk kthehet."
        details={userToDelete ? `${[userToDelete.firstName, userToDelete.lastName].filter(Boolean).join(' ') || userToDelete.userName} · ${userToDelete.email}` : ''}
        confirmLabel="Fshi përgjithmonë"
        busy={userDeleting}
        onConfirm={deleteUser}
        onClose={() => setUserToDelete(null)}
      />

      <ConfirmDialog
        open={Boolean(roleToDelete)}
        title="Fshi rolin"
        message="Përdoruesit që e kanë këtë rol kthehen te lejet e rolit të tyre bazë."
        details={roleToDelete ? roleToDelete.name : ''}
        confirmLabel="Fshi"
        busy={customRoleSaving}
        onConfirm={() => deleteCustomRole(roleToDelete.id)}
        onClose={() => setRoleToDelete(null)}
      />

      <Dialog open={customRoleDialog.open} onClose={closeCustomRoleDialog} maxWidth="md" fullWidth PaperProps={{ sx:{ borderRadius:'12px', overflow:'hidden' } }}>
        {customRoleDialog.role && (()=>{
          const cr    = customRoleDialog.role;
          const setCR = (patch) => setCustomRoleDialog(p=>({...p, role:{...p.role,...patch}}));
          const setPermission = (mod, val) => setCustomRoleDialog(p=>({ ...p, role:{ ...p.role, permissions:{ ...(p.role.permissions||{}), [mod]:val } } }));
          const isNew = !cr.id;
          return (
            <>
              <Box sx={{ height:5, bgcolor:cr.color||'#7C3AED' }}/>
              <Box sx={{ px:3, pt:2, pb:1.5, display:'flex', alignItems:'center', gap:1.5, borderBottom:'1px solid', borderColor:'divider' }}>
                <Avatar sx={{ bgcolor:cr.color||'#7C3AED', width:40, height:40 }}><WorkspacePremiumIcon sx={{ fontSize:20 }}/></Avatar>
                <Box sx={{ flex:1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>{isNew?'Shto rol me privilegje':`Edito: ${cr.name}`}</Typography>
                  <Typography variant="caption" color="text.secondary">{isNew?'Krijo rol të personalizuar me privilegje të detajuara':'Ndrysho emrin, përshkrimin dhe privilegjet'}</Typography>
                </Box>
              </Box>
              <DialogContent sx={{ pt:2.5, pb:1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform:'uppercase', letterSpacing:0.5, display:'block', mb:1.5 }}>Informacioni i rolit</Typography>
                    <Stack spacing={2}>
                      <TextField size="small" fullWidth label="Emri i rolit *" placeholder="p.sh. Menaxher Rajonesh" value={cr.name} onChange={e=>setCR({name:e.target.value})}/>
                      <TextField size="small" fullWidth label="Përshkrimi" placeholder="Çfarë bën ky rol?" multiline minRows={3} value={cr.description} onChange={e=>setCR({description:e.target.value})}/>
                      <Box>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display:'block', mb:1 }}>Ngjyra e rolit</Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb:1 }}>
                          {CUSTOM_ROLE_COLORS.map(c=>(
                            <Box key={c} onClick={()=>setCR({color:c})} sx={{ width:22, height:22, borderRadius:'50%', cursor:'pointer', bgcolor:c, border:cr.color===c?'3px solid #000':'2px solid transparent', outline:cr.color===c?`2px solid ${c}`:'none', transition:'all .1s' }}/>
                          ))}
                        </Stack>
                        <TextField type="color" size="small" fullWidth label="Ngjyrë e personalizuar" value={cr.color||'#7C3AED'} onChange={e=>setCR({color:e.target.value})} inputProps={{ style:{ padding:3, height:28 } }}/>
                      </Box>
                      <TextField select size="small" fullWidth label="Fushëveprimi" value={cr.scope||'company'} onChange={e=>setCR({scope:e.target.value})}>
                        <MenuItem value="company">I gjithë kompania</MenuItem>
                        <MenuItem value="department">Departamenti</MenuItem>
                        <MenuItem value="branch">Filiali</MenuItem>
                        <MenuItem value="team">Ekipi i drejtpërdrejtë</MenuItem>
                      </TextField>
                      <Box>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display:'block', mb:1 }}>Ngarko nga roli bazë</Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {ROLES_RBAC.map(r=>(
                            <Chip key={r.id} size="small" label={r.name} clickable onClick={()=>setCR({permissions:{...ROLE_DEFAULT_PERMS[r.id]}})}
                              sx={{ fontSize:11, height:24, bgcolor:r.color+'15', color:r.color, border:`1px solid ${r.color}40`, fontWeight:600, '&:hover':{ bgcolor:r.color+'30' } }}/>
                          ))}
                          <Chip size="small" label="Pastro gjithë" clickable onClick={()=>setCR({permissions:{}})}
                            sx={{ fontSize:11, height:24, color:'text.secondary', '&:hover':{ bgcolor:'error.50', color:'error.main' } }}/>
                        </Stack>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb:1.5 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform:'uppercase', letterSpacing:0.5 }}>Privilegjet sipas modulit</Typography>
                      <Typography variant="caption" color="text.disabled">Klikoni opsionin e dëshiruar</Typography>
                    </Stack>
                    {/* Një rol i ndërtuar te «Rolet & Privilegjet» i ruan lejet si
                        listë e detajuar (`employees.view.company`). Ky editor njeh
                        vetëm nivele për modul, ndaj i shfaqte të gjitha si «Asnjë»
                        dhe ruajtja i fshinte pa asnjë paralajmërim. */}
                    {Array.isArray(cr.permissions) ? (
                      <Alert severity="warning" sx={{ mb: 1.5 }}>
                        Ky rol është konfiguruar te skeda <b>Rolet &amp; Privilegjet</b>, me privilegje të detajuara
                        ({cr.permissions.length}). Redaktoji atje — këtu do të zëvendësoheshin me nivele të përgjithshme.
                      </Alert>
                    ) : (
                      <PermissionEditor permissions={cr.permissions} onChange={setPermission}/>
                    )}
                    <Box sx={{ mt:1.5, p:1.5, bgcolor:'grey.50', borderRadius:'8px' }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display:'block', mb:0.75 }}>Rezyme</Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {!Array.isArray(cr.permissions) && MODULE_DEFS.map(mod=>{ const val=cr.permissions?.[mod.key]; if(!val||val==='none') return null; const lvl=mod.levels.find(l=>l.value===val); return lvl?<Chip key={mod.key} size="small" label={`${mod.label} → ${lvl.label}`} sx={{ fontSize:10, height:20, bgcolor:lvl.color+'15', color:lvl.color, border:`1px solid ${lvl.color}30`, fontWeight:600 }}/>:null; })}
                        {!Array.isArray(cr.permissions) && Object.values(cr.permissions||{}).every(v=>!v||v==='none')&&<Typography variant="caption" color="text.disabled" sx={{ fontStyle:'italic' }}>Asnjë privilegj i zgjedhur</Typography>}
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ px:3, pb:2.5, pt:2, borderTop:'1px solid', borderColor:'divider', gap:1 }}>
                <Button onClick={closeCustomRoleDialog} variant="outlined" sx={{ borderRadius:'8px', textTransform:'none' }}>Anulo</Button>
                <Button onClick={saveCustomRole} variant="contained" disabled={customRoleSaving}
                  startIcon={customRoleSaving?<CircularProgress size={14} color="inherit"/>:<SaveIcon/>}
                  sx={{ borderRadius:'8px', textTransform:'none', fontWeight:600, minWidth:160 }}>
                  {customRoleSaving ? 'Duke ruajtur...' : isNew ? 'Krijo rolin' : 'Ruaj ndryshimet'}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      <Dialog open={!!manualInvite.link} onClose={closeInviteLink} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:'12px' } }}>
        <DialogTitle sx={{ pb:1 }}>
          <Typography component="div" sx={{ fontWeight:700, fontSize:18 }}>
            Email-i i ftesës nuk u dërgua
          </Typography>
          {(manualInvite.name || manualInvite.email) && (
            <Typography component="div" variant="body2" color="text.secondary">
              {[manualInvite.name, manualInvite.email].filter(Boolean).join(' · ')}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb:1.5, borderRadius:'8px' }}>{manualInvite.message}</Alert>
          <TextField fullWidth size="small" label="Linku i ftesës" value={manualInvite.link}
            InputProps={{ readOnly:true }} onFocus={e => e.target.select()} />
          <Typography variant="caption" color="text.secondary" sx={{ display:'block', mt:1 }}>
            Linku është personal — kushdo që e ka mund ta aktivizojë llogarinë. Jepjani vetëm personit që i takon.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2.5, gap:1, flexWrap:'wrap' }}>
          <Button onClick={closeInviteLink} sx={{ textTransform:'none', mr:'auto' }}>Mbyll</Button>
          <Button variant="contained" disableElevation startIcon={<ContentCopyIcon />} sx={{ textTransform:'none', borderRadius:'8px' }} onClick={async () => {
            try { await navigator.clipboard.writeText(manualInvite.link); showSnack('Linku u kopjua.'); }
            catch { showSnack('Kopjimi dështoi. Zgjidhe dhe kopjoje linkun nga fusha.', 'warning'); }
          }}>Kopjo linkun</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={()=>setSnack('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackType} onClose={()=>setSnack('')} sx={{ borderRadius:'8px' }}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function UserManagementPage() {
  const location = useLocation();
  const history  = useHistory();

  // Tab-i aktiv vjen nga URL (?tab=) — kështu /roles-permissions dhe /positions
  // ridrejtohen këtu duke hapur direkt tab-in e duhur, dhe deep-link/back funksionojnë.
  const tabFromUrl = parseInt(new URLSearchParams(location.search).get('tab'), 10);
  const outerTab   = [0, 1, 2].includes(tabFromUrl) ? tabFromUrl : 0;
  const setOuterTab = (v) => history.replace(`/user-management${v ? `?tab=${v}` : ''}`);

  return (
    <Box sx={{ width:'100%', minWidth:0, boxSizing:'border-box', px:0, py:0, maxWidth:1540, mx:'auto' }}>
      <Box sx={{ mb:{ xs:2, md:2.5 } }}>
        <Typography sx={{ color:'#256878', fontSize:11, fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', mb:0.65 }}>
          Administrimi i qasjeve
        </Typography>
        <Typography component="h1" sx={{ color:'#0F172A', fontSize:{ xs:23, sm:27 }, fontWeight:800, letterSpacing:'-0.035em', lineHeight:1.2 }}>
          Përdoruesit dhe qasjet
        </Typography>
        <Typography sx={{ color:'#64748B', fontSize:{ xs:13, sm:14 }, mt:0.7, maxWidth:700, lineHeight:1.55 }}>
          Menaxho llogaritë, rolet dhe lejet e punës nga një vend i qartë.
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ minWidth:0, border:'1px solid #E2E8F0', borderRadius:'12px', overflow:'hidden', boxShadow:'0 10px 32px rgba(15,23,42,0.04)' }}>
        <Tabs
          value={outerTab}
          onChange={(_, v) => setOuterTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px:{ xs:0.5, sm:1.5 }, bgcolor:'#F8FAFC', borderBottom:'1px solid #E2E8F0', minHeight:46,
            '& .MuiTab-root':{ textTransform:'none', fontWeight:650, fontSize:{ xs:12.5, sm:14 }, minHeight:46, color:'#64748B', px:{ xs:1.5, sm:2.5 }, whiteSpace:'nowrap' },
            '& .Mui-selected':{ fontWeight:750, color:'#256878' },
            '& .MuiTabs-indicator':{ height:3, borderRadius:'3px 3px 0 0', bgcolor:'#3E8FA3' },
          }}
        >
          <Tab icon={<GroupIcon sx={{ fontSize:17 }}/>} iconPosition="start" label="Përdoruesit"/>
          <Tab icon={<SecurityIcon sx={{ fontSize:17 }}/>} iconPosition="start" label="Rolet & Privilegjet"/>
          <Tab icon={<BadgeIcon sx={{ fontSize:17 }}/>} iconPosition="start" label="Pozitat & Qasjet"/>
        </Tabs>

        <Box sx={{ p:{ xs:1.25, sm:2 } }}>
          {outerTab === 0 && <UsersPanel/>}
          {outerTab === 1 && <RolesPermissionsPage embedded/>}
          {outerTab === 2 && <PositionsPage embedded/>}
        </Box>
      </Paper>
    </Box>
  );
}
